import { z } from 'zod';
import { coSellMsftApi } from '../tackleApiClient';
import { useQuery } from '@tanstack/react-query';
import useMsftVendorSettings from 'packages/cosell/src/pages/microsoft/hooks/useMsftVendorSettings';

const BaseSolution = z.object({
  tackleId: z.string(),
  cloudProviderId: z.string(),
  sourceAccount: z.string(),
  name: z.string(),
  solutionId: z.string(),
  referralOrganizationId: z.string(),
  displayOrganizationId: z.string(),
  publisherName: z.string(),
  mpnId: z.string(),
  /** find out type of incentive */
  incentives: z.nullable(z.array(z.string())).default([]),
  created: z.string().datetime(),
  lastSynced: z.string().datetime(),
});

const ThirdPartySolution = BaseSolution.extend({
  solutionType: z.literal('3P'),
});

const MsftSolution = BaseSolution.extend({
  solutionType: z.literal('1P'),
});

const SolutionSchema = z.discriminatedUnion('solutionType', [
  ThirdPartySolution,
  MsftSolution,
]);

const solutions_Response = z
  .object({
    solutions: z.array(SolutionSchema),
    total: z.number(),
    currentPageCount: z.number(),
    isNextPage: z.boolean(),
    nextIndex: z.number().int().optional(),
  })
  .partial()
  .strict()
  .passthrough();

export const schemas = {
  solutions_Response,
  SolutionSchema,
};

export type Solution = z.infer<typeof SolutionSchema>;
export type SolutionsResponse = z.infer<typeof solutions_Response>;

/**
 * Makes a GET request to the api/solutions endpoint.
 * vendor data is picked from the bearer token
 * @param  - none.
 * @returns A promise resolving to the response adhering to the `solutions_Response` schema.
 */
export async function getSolutions() {
  const response = await coSellMsftApi.get('api/solutions').json();
  const parsedResponse = solutions_Response.parse(response);
  return parsedResponse;
}

/**
 * Makes a GET request to the api/solutions endpoint.
 * vendor data is picked from the bearer token
 * @param  - none.
 * @returns A promise resolving to the response adhering to the `solutions_Response` schema.
 */
export async function getSolutionsByOrgId({
  referralOrganizationId,
}: {
  referralOrganizationId: string;
}) {
  const queryParams = new URLSearchParams({
    referralOrganizationID: referralOrganizationId,
  });
  const response = await coSellMsftApi
    .get(`api/solutions?${queryParams?.toString()}`)
    .json();
  const parsedResponse = solutions_Response.parse(response);
  return parsedResponse;
}

export const msftSolutionsApiToFormFormatter = ({
  data,
}: {
  data: SolutionsResponse;
}) => {
  if (!data?.solutions?.length) {
    return [];
  }

  return data?.solutions?.map((solution) => ({
    ...solution,
    value: solution?.solutionId,
    display: solution?.name || 'Name not found',
  }));
};

export function useGetMsftVendorSolutionsOptions({
  enabled,
}: {
  enabled?: boolean;
}) {
  const { data: vendorSettings } = useMsftVendorSettings();
  const referralOrganizationId = vendorSettings?.tenantId;

  return useQuery({
    /**
     *  GET /solutions endpoint grabs the vendorId from the token, so vendorId is not required/passed in
     */
    /*
     * enabled is passed an an optional parameter to conditionally fetch
     */
    queryKey: [
      'msft-co-sell-vendor-solutions-options-by-org-id',
      referralOrganizationId,
    ],
    queryFn: () => getSolutionsByOrgId({ referralOrganizationId }),
    /* using the selector to transform the response data to standardize with picklist/default value enum map shapes */
    select: (data) => {
      return { solutions: msftSolutionsApiToFormFormatter({ data }) };
    },
    staleTime: Infinity,
    enabled: !!enabled,
  });
}
